<!-- An empty tab says where the matches are ("2 matches in People") instead
     of only offering Ask. Outside the listbox on purpose: a listbox may only
     own options, and these are buttons that switch the chip. -->
@if (hints().length > 0) {
  <div
    class="cmdk-results__hints"
    role="group"
    data-testid="cmdk-scope-hints"
    [attr.aria-label]="hintsHeading()">
    <p class="cmdk-results__hints-heading">{{ hintsHeading() }}</p>
    <div class="cmdk-results__hints-list">
      @for (hint of hints(); track hint.scope) {
        <paper-button
          intent="secondary"
          size="sm"
          icon="arrow-right"
          iconPosition="right"
          [data-testid]="'cmdk-scope-hint-' + hint.scope"
          (click)="showScope(hint.scope)">
          {{ hintLabel(hint) }}
        </paper-button>
      }
    </div>
  </div>
}

<div
  #scroller
  class="cmdk-results"
  role="listbox"
  [id]="listboxId()"
  [attr.aria-label]="listLabel()"
  data-testid="cmdk-results">

  @for (row of rows(); track $index) {
    @switch (row.type) {

      @case ('header') {
        <p class="cmdk-results__header" role="presentation" [attr.data-testid]="'cmdk-group-' + row.group">{{ row.label }}</p>
      }

      @case ('skeleton') {
        <div class="cmdk-results__state" role="presentation">
          <paper-state state="loading" skeleton="row" [skeletonRows]="row.count" size="sm" />
        </div>
      }

      @case ('error') {
        <!-- paper-state renders a Try-again affordance; wire it, or remove it.
             A retry that does nothing is worse than no retry — the user
             concludes the data is genuinely gone. -->
        <div class="cmdk-results__state" [attr.data-testid]="'cmdk-group-error-' + row.group">
          <paper-state state="error" size="sm" (ctaClick)="retryGroup(row.group)" />
        </div>
      }

      @case ('empty') {
        <!-- Reached when nothing matched AND there was no Ask row to fall back
             on. Before this existed the column rendered nothing at all, which
             read as the palette having broken rather than the query having
             missed. Not selectable — there is nothing here to open. -->
        <div class="cmdk-results__state" data-testid="cmdk-no-matches" role="presentation">
          <paper-state
            state="empty"
            size="sm"
            icon="search"
            [title]="emptyTitle()"
            [description]="emptyDescription()" />
        </div>
      }

      @case ('row') {
        <!-- role="option" inside a listbox: focus stays in the input and the
             combobox points here with aria-activedescendant — so this is a div with
             role="option", never a button element. -->
        <div
          class="cmdk-results__row"
          role="option"
          [id]="row.domId"
          [attr.data-flat-index]="row.flatIndex"
          [attr.data-testid]="'cmdk-result-' + row.item.kind"
          [attr.data-key]="row.item.id"
          [attr.data-kind]="row.item.kind"
          [attr.data-selected]="isSelected(row)"
          [attr.aria-selected]="isSelected(row)"
          [class.is-selected]="isSelected(row)"
          (click)="onClick(row)"
          (mouseenter)="onHover(row)">
          @switch (row.item.kind) {
            @case ('nav') {
              <im-cmdk-row-command
                [item]="asCommandItem(row.item)"
                [selected]="isSelected(row)"
                [quickOpenIndex]="modifierHeld() ? (quickOpenByFlatIndex().get(row.flatIndex) ?? null) : null" />
            }
            @case ('action') {
              <im-cmdk-row-command
                [item]="asCommandItem(row.item)"
                [selected]="isSelected(row)"
                [quickOpenIndex]="modifierHeld() ? (quickOpenByFlatIndex().get(row.flatIndex) ?? null) : null" />
            }
            @case ('ask') {
              <im-cmdk-row-ask [item]="asAskItem(row.item)" [selected]="isSelected(row)" />
            }
            @case ('space') {
              <im-cmdk-row-space
                [item]="asSpaceItem(row.item)"
                [selected]="isSelected(row)"
                [quickOpenIndex]="modifierHeld() ? (quickOpenByFlatIndex().get(row.flatIndex) ?? null) : null" />
            }
            @case ('person') {
              <im-cmdk-row-person
                [item]="asPersonItem(row.item)"
                [selected]="isSelected(row)"
                [quickOpenIndex]="modifierHeld() ? (quickOpenByFlatIndex().get(row.flatIndex) ?? null) : null" />
            }
            @case ('news') {
              <im-cmdk-row-news
                [item]="asNewsItem(row.item)"
                [selected]="isSelected(row)"
                [quickOpenIndex]="modifierHeld() ? (quickOpenByFlatIndex().get(row.flatIndex) ?? null) : null" />
            }
            @case ('app') {
              <im-cmdk-row-app
                [item]="asAppItem(row.item)"
                [selected]="isSelected(row)"
                [quickOpenIndex]="modifierHeld() ? (quickOpenByFlatIndex().get(row.flatIndex) ?? null) : null" />
            }
          }
        </div>
      }
    }
  }
</div>
